import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import useAnchoredPanel from "./useAnchoredPanel";

/**
 * A date field that can be typed into or picked from a calendar.
 *
 * Ported from the VAV central ERP's `DatePicker`, keeping the part that makes
 * it work for a date of birth: the header drills down. The month opens a grid
 * of months and the year opens a page of twenty years, so a date decades back
 * is three clicks rather than four hundred presses of a back arrow. What is
 * not carried over is its inline styling — this one is built out of the same
 * Tailwind, lucide icons and portalled panel as `CustomDropdown` beside it.
 *
 * The value is the app's own `DD/MM/YYYY`, not an ISO string: that is what the
 * form holds, what its validation reads, and what the field displays, so the
 * calendar is an alternative way of filling the same box rather than a second
 * format to convert between.
 */

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTHS_SHORT = MONTHS.map((month) => month.slice(0, 3));

/** How many years the year grid shows at once. */
const YEAR_PAGE = 20;

const PANEL_WIDTH = 288;
/** Only used to decide whether the panel has room below its field. */
const PANEL_HEIGHT = 348;

type PanelView = "days" | "months" | "years";

/** Midnight, so two dates compare on the day rather than the moment. */
const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * `DD/MM/YYYY` into a date, or `null` for anything part-typed or impossible.
 * Reading the parts back off is what rejects 31/02 — `new Date(2026, 1, 31)`
 * rolls forward to March rather than failing.
 */
export const parseDisplayDate = (value: string): Date | null => {
  const match = DATE_PATTERN.exec((value ?? "").trim());
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return atMidnight(date);
};

/** A date back into the `DD/MM/YYYY` the field holds. */
export const formatDisplayDate = (date: Date): string =>
  [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getFullYear()),
  ].join("/");

export interface DatePickerProps {
  /** `DD/MM/YYYY`, or empty. */
  value: string;
  /** Fires for both a typed keystroke and a picked day. */
  onValueChange: (value: string) => void;
  /** Runs the typed text through the form's normaliser before it lands. */
  normalise?: (raw: string) => string;
  placeholder?: string;
  /** Marks the field visited — fires when the panel closes, however it closed. */
  onBlur?: () => void;
  invalid?: boolean;
  disabled?: boolean;
  /** Days outside these are shown but cannot be picked. */
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  className?: string;
}

const navButtonClasses =
  "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border " +
  "border-line bg-white text-ink-muted transition-colors hover:bg-[#F4F7F8] " +
  "disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white";

/** The month/year buttons in the header, and every cell of the two grids. */
const headerLabelClasses =
  "rounded-md px-2 py-0.5 text-[14px] font-medium transition-colors hover:bg-brand-soft";

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onValueChange,
  normalise,
  placeholder = "DD/MM/YYYY",
  onBlur,
  invalid = false,
  disabled = false,
  minDate,
  maxDate,
  id,
  className = "",
  ...aria
}) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<PanelView>("days");

  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const { rect, openUpward } = useAnchoredPanel(anchorRef, open, PANEL_HEIGHT);

  const selected = useMemo(() => parseDisplayDate(value), [value]);

  /**
   * Where the calendar opens when the field is empty: the latest date that can
   * be picked, so a date of birth starts at the newest allowed year rather
   * than at today and a page of years that are all out of range.
   */
  const fallbackMonth = useMemo(
    () => atMidnight(maxDate ?? new Date()),
    [maxDate]
  );

  const [month, setMonth] = useState(() => {
    const from = selected ?? fallbackMonth;
    return new Date(from.getFullYear(), from.getMonth(), 1);
  });
  const [yearStart, setYearStart] = useState(
    () =>
      Math.floor((selected ?? fallbackMonth).getFullYear() / YEAR_PAGE) *
      YEAR_PAGE
  );

  const close = useCallback(() => {
    setOpen(false);
    setView("days");
    onBlur?.();
  }, [onBlur]);

  /** Opens on the month the field already names, wherever it was left last. */
  const openPanel = useCallback(() => {
    if (disabled) return;
    const from = parseDisplayDate(value) ?? fallbackMonth;
    setMonth(new Date(from.getFullYear(), from.getMonth(), 1));
    setYearStart(Math.floor(from.getFullYear() / YEAR_PAGE) * YEAR_PAGE);
    setView("days");
    setOpen(true);
  }, [disabled, value, fallbackMonth]);

  // A pointer press outside both the field and the panel dismisses it.
  // `mousedown` rather than `click`, so the panel is gone before a click lands
  // on whatever was underneath it.
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !anchorRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        close();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, close]);

  const outOfRange = (date: Date): boolean => {
    const day = atMidnight(date);
    if (minDate && day < atMidnight(minDate)) return true;
    if (maxDate && day > atMidnight(maxDate)) return true;
    return false;
  };

  const pick = (date: Date) => {
    onValueChange(formatDisplayDate(date));
    close();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && open) {
      event.stopPropagation();
      close();
      return;
    }
    // The keyboard's version of reaching for the calendar.
    if (!open && (event.key === "ArrowDown" || event.key === "Enter")) {
      // Enter in a form field submits it; only the arrow opens the panel.
      if (event.key === "ArrowDown") {
        event.preventDefault();
        openPanel();
      }
    }
  };

  /* ── The three views ───────────────────────────────────────────────────── */

  const renderDays = () => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const leading = new Date(year, monthIndex, 1).getDay();
    const today = atMidnight(new Date());

    const cells: React.ReactNode[] = [];
    for (let blank = 0; blank < leading; blank += 1) {
      cells.push(<span key={`blank-${blank}`} aria-hidden="true" />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, monthIndex, day);
      const unavailable = outOfRange(date);
      const isSelected = !!selected && selected.getTime() === date.getTime();
      const isToday = today.getTime() === date.getTime();

      cells.push(
        <button
          key={day}
          type="button"
          disabled={unavailable}
          aria-pressed={isSelected}
          aria-label={`${day} ${MONTHS[monthIndex]} ${year}`}
          onClick={() => pick(date)}
          className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full text-[13px] transition-colors disabled:cursor-not-allowed disabled:text-ink-subtle disabled:opacity-50 ${
            isSelected
              ? "bg-brand font-semibold text-white"
              : isToday
              ? "border border-brand font-semibold text-brand-dark hover:bg-brand-soft"
              : "text-ink hover:bg-brand-soft"
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <>
        <div className="mb-1 grid grid-cols-7 gap-1">
          {WEEKDAYS.map((weekday) => (
            <span
              key={weekday}
              aria-hidden="true"
              className="flex h-7 items-center justify-center text-[11px] font-medium text-ink-muted"
            >
              {weekday}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">{cells}</div>
      </>
    );
  };

  const renderMonths = () => (
    <div className="grid grid-cols-4 gap-1.5">
      {MONTHS_SHORT.map((short, index) => {
        const active = index === month.getMonth();
        return (
          <button
            key={short}
            type="button"
            aria-label={MONTHS[index]}
            aria-pressed={active}
            onClick={() => {
              setMonth(new Date(month.getFullYear(), index, 1));
              setView("days");
            }}
            className={`rounded-md py-2 text-[13px] transition-colors ${
              active
                ? "bg-brand-soft font-semibold text-brand-dark"
                : "text-ink hover:bg-brand-soft"
            }`}
          >
            {short}
          </button>
        );
      })}
    </div>
  );

  const renderYears = () => (
    <div className="grid grid-cols-4 gap-1.5">
      {Array.from({ length: YEAR_PAGE }, (_, index) => yearStart + index).map(
        (year) => {
          const active = year === month.getFullYear();
          return (
            <button
              key={year}
              type="button"
              aria-pressed={active}
              onClick={() => {
                setMonth(new Date(year, month.getMonth(), 1));
                setView("months");
              }}
              className={`rounded-md py-2 text-[13px] transition-colors ${
                active
                  ? "bg-brand-soft font-semibold text-brand-dark"
                  : "text-ink hover:bg-brand-soft"
              }`}
            >
              {year}
            </button>
          );
        }
      )}
    </div>
  );

  /** Back and forward mean a month, a year or a page of years, by view. */
  const step = (direction: -1 | 1) => {
    if (view === "days") {
      setMonth(
        new Date(month.getFullYear(), month.getMonth() + direction, 1)
      );
      return;
    }
    if (view === "months") {
      setMonth(
        new Date(month.getFullYear() + direction, month.getMonth(), 1)
      );
      return;
    }
    setYearStart((current) => current + direction * YEAR_PAGE);
  };

  const panel =
    open && rect
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Choose a date"
            className="z-[60] rounded-md border border-line bg-white p-3 shadow-[0_8px_24px_rgba(11,27,31,0.12)]"
            style={{
              position: "fixed",
              width: PANEL_WIDTH,
              // Clamped to the viewport: the field can sit in the last column
              // of the grid, where its left edge leaves no room for the panel.
              left: Math.max(
                8,
                Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)
              ),
              ...(openUpward
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
            }}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                aria-label="Previous"
                onClick={() => step(-1)}
                className={navButtonClasses}
              >
                <ChevronLeft aria-hidden="true" className="h-4 w-4" />
              </button>

              {view === "years" ? (
                <span className="text-[14px] font-medium text-ink">
                  {yearStart} - {yearStart + YEAR_PAGE - 1}
                </span>
              ) : (
                <div className="flex items-center gap-1">
                  {view === "days" && (
                    <button
                      type="button"
                      onClick={() => setView("months")}
                      className={`${headerLabelClasses} text-ink`}
                    >
                      {MONTHS[month.getMonth()]}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setView("years")}
                    className={`${headerLabelClasses} text-brand-dark`}
                  >
                    {month.getFullYear()}
                  </button>
                </div>
              )}

              <button
                type="button"
                aria-label="Next"
                onClick={() => step(1)}
                className={navButtonClasses}
              >
                <ChevronRight aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>

            {view === "days" && renderDays()}
            {view === "months" && renderMonths()}
            {view === "years" && renderYears()}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={anchorRef} className={`relative ${className}`}>
      <div
        className={
          "flex w-full items-center gap-2 rounded-[6px] border bg-white px-3.5 py-2.5 3xl:py-3 " +
          "transition-colors " +
          (disabled ? "cursor-not-allowed bg-[#F7F9FA] " : "") +
          (invalid
            ? "border-red-400 focus-within:border-red-500"
            : "border-line focus-within:border-brand")
        }
      >
        {/* Still an input: a date of birth is quicker typed than hunted for,
            and the normaliser types the slashes as it goes. The calendar is
            the other way in, not a replacement. */}
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          maxLength={10}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={(event) =>
            onValueChange(
              normalise ? normalise(event.target.value) : event.target.value
            )
          }
          onClick={openPanel}
          onKeyDown={handleKeyDown}
          // Only when the panel is shut — otherwise `close` reports it, once.
          onBlur={() => !open && onBlur?.()}
          className="w-full bg-transparent text-[14px] 3xl:text-[15px] text-ink placeholder:text-ink-subtle focus:outline-none disabled:cursor-not-allowed disabled:text-ink-muted"
          {...aria}
        />
        <button
          type="button"
          aria-label="Open calendar"
          aria-expanded={open}
          disabled={disabled}
          // Ahead of focus, so pressing the icon while the panel is open shuts
          // it rather than closing and immediately reopening on focus.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (open ? close() : openPanel())}
          className="flex-shrink-0 text-ink-muted transition-colors hover:text-ink disabled:cursor-not-allowed"
        >
          <CalendarIcon aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      {panel}
    </div>
  );
};

export default DatePicker;
